import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { isAPIError } from '@/store/utils';
import { withErrorHandling, withLoader } from '@/store/thunks/thunkWrappers';
import { getUserIdFromToken } from '@/store/utils/jwtDecoder';
import { getRegistrationUsername } from '@/utils/sessionStorage';
import type { PasswordSetupPayload } from '@/types/AuthState';

/**
 * Registration-only password setup thunk.
 *
 * Two phases:
 *  - preCommit: token/username guards + the setPassword API call. Failures here
 *    are recoverable; the wrapper produces a standard rejection with no `stage`.
 *  - postCommit: ukek derivation, mlkem keypair generation, encrypt, and the
 *    mlkem POST. A failure here is catastrophic — the password is already
 *    changed server-side but local setup did not finish — so we reject with
 *    `stage: 'postCommit'` and the UI surfaces a severe message directing the
 *    user to support.
 *
 * State writes happen in `extraReducers` from the returned `PasswordSetupPayload`.
 */
export const registerPasswordThunk = createAppAsyncThunk<PasswordSetupPayload, { password: string }>(
  'auth/registerPassword',
  withLoader(withErrorHandling(async(
    { password },
    { getState, extra: { authService, pocServices: { cryptoService, mlkemKeysService } }, rejectWithValue }
  ) => {
    const state = getState();
    const username = getRegistrationUsername();

    if (typeof username !== 'string' || username.trim().length === 0) {
      return rejectWithValue({ message: 'Username is missing. Please restart the flow.' });
    }
    const token = state.auth.registrationToken;
    if (token === undefined) {
      return rejectWithValue({ message: 'Missing registration or recovery token.' });
    }

    const normalizedUsername = username.toLowerCase();

    try {
      await authService.setPassword({ username: normalizedUsername, password });
    } catch (err) {
      if (isAPIError(err)) throw err;
      return rejectWithValue({ message: 'Something went wrong. Please try again.' });
    }

    try {
      const userId = getUserIdFromToken(token);
      if (userId === null) {
        return rejectWithValue({
          message: 'Your password was updated, but we couldn\'t finish setting up your account.',
          stage: 'postCommit',
        });
      }
      const ukek = await cryptoService.deriveKey(password, normalizedUsername);
      const hashResult = await cryptoService.hashPassword(password, normalizedUsername);
      const keyPair = await cryptoService.generateMlKemKeyPairFromCredentials(normalizedUsername, password);
      const encryptedPrivateKey = await cryptoService.encryptPrivateKey(keyPair.privateKey, hashResult);

      const mlkemPayload = {
        encapsKey: JSON.stringify(Array.from(keyPair.publicKey)),
        decapsKey: encryptedPrivateKey,
      };
      await mlkemKeysService.registerMlkemKeys(userId, mlkemPayload, token);

      return {
        userId,
        ukek,
        derivedKey: hashResult.keyHex,
        keyPair,
        encryptedPrivateKey,
      };
    } catch {
      return rejectWithValue({
        message: 'Your password was updated, but we couldn\'t finish setting up your account.',
        stage: 'postCommit',
      });
    }
  }))
);
